/**
 * useSystemHealth — Hook React para monitoramento de saúde dos serviços
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Faz polling em /api/system/health a cada `intervalMs` (padrão: 30s).
 * Dispara um callback onStatusChange quando qualquer serviço muda de estado.
 *
 * Retorna:
 *   health       — objeto completo retornado pela API
 *   overall      — 'online' | 'degraded' | 'offline' | 'loading'
 *   services     — array de serviços com status individual
 *   lastChecked  — Date da última verificação
 *   checking     — boolean (true durante o fetch)
 *   refresh      — função para forçar atualização imediata
 *
 * ── Onda 60: o laço de re-fetch ────────────────────────────────────────────
 *
 * Este hook pedia saúde 43 VEZES POR SEGUNDO, e não a cada 30 s. Medido no G2
 * em 09/09/2026: 2.593 chamadas a `/api/system/health` em um único minuto.
 *
 * O ciclo era este. `onStatusChange` chega como uma função nova a cada render
 * do componente que usa o hook (nenhum dos dois a memoriza). Ela entrava nas
 * dependências do `useCallback`, então `fetchHealth` também era novo a cada
 * render; e `fetchHealth` entrava nas dependências do `useEffect`, que então
 * reiniciava — e reiniciar o efeito **dispara um fetch imediato**. Como cada
 * resposta chama `setLastChecked(new Date())`, que é sempre um objeto novo, o
 * render acontecia sempre, e o ciclo se fechava:
 *
 *   fetch → setState → render → nova callback → novo fetchHealth →
 *   efeito reinicia → fetch imediato → ...
 *
 * O custo não era só a tela tremendo: cada chamada faz `SELECT 1`, consulta as
 * câmeras e dispara DUAS requisições HTTP ao motor ALPR — o mesmo motor que
 * processa os frames. Eram ~86 requisições por segundo competindo com o
 * reconhecimento.
 *
 * A correção tem três partes, e as três são necessárias:
 *   1. `onStatusChange` vive numa ref e sai das dependências — trocar a
 *      identidade da callback não pode mais reiniciar o polling;
 *   2. `fetchHealth` passa a ser estável (`useCallback` com `[]`), então o
 *      efeito só roda quando `intervalMs` muda de verdade;
 *   3. uma guarda contra chamadas simultâneas, para o caso de um `refresh`
 *      manual coincidir com o tique do intervalo.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

export default function useSystemHealth({ intervalMs = 30000, onStatusChange } = {}) {
  const [health,      setHealth]      = useState(null);
  const [overall,     setOverall]     = useState('loading');
  const [services,    setServices]    = useState([]);
  const [lastChecked, setLastChecked] = useState(null);
  const [checking,    setChecking]    = useState(false);

  // Rastrear o estado anterior para detectar transições online → offline
  const prevOverallRef = useRef(null);

  // A callback do chamador é recriada a cada render dele. Guardá-la aqui é o
  // que a mantém fora das dependências — e o que quebra o laço.
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; });

  // Uma chamada por vez: o `refresh` manual pode coincidir com o tique.
  const emAndamentoRef = useRef(false);
  // Depois de desmontado, nada de setState — evita o aviso do React quando a
  // resposta chega com a tela já trocada.
  const montadoRef = useRef(true);

  const fetchHealth = useCallback(async () => {
    if (emAndamentoRef.current) return;
    emAndamentoRef.current = true;
    setChecking(true);
    try {
      const resp = await api.get('/api/system/health');
      const data = resp.data;
      if (!montadoRef.current) return;

      setHealth(data);
      setOverall(data.overall || 'offline');
      setServices(Array.isArray(data.services) ? data.services : []);
      setLastChecked(new Date());

      // Notificar mudança de estado
      const notificar = onStatusChangeRef.current;
      if (notificar && prevOverallRef.current !== null && prevOverallRef.current !== data.overall) {
        notificar({ previous: prevOverallRef.current, current: data.overall, data });
      }
      prevOverallRef.current = data.overall;
    } catch (err) {
      if (!montadoRef.current) return;
      // Backend inacessível — marcar tudo como offline
      setOverall('offline');
      setServices(prev => prev.map(s => ({ ...s, status: 'offline' })));
      const notificar = onStatusChangeRef.current;
      if (notificar && prevOverallRef.current !== 'offline') {
        notificar({ previous: prevOverallRef.current, current: 'offline', error: err });
      }
      prevOverallRef.current = 'offline';
    } finally {
      emAndamentoRef.current = false;
      if (montadoRef.current) setChecking(false);
    }
  }, []); // estável de propósito: ver o comentário do cabeçalho

  useEffect(() => {
    montadoRef.current = true;
    fetchHealth();
    const timer = setInterval(fetchHealth, intervalMs);
    return () => {
      montadoRef.current = false;
      clearInterval(timer);
    };
  }, [fetchHealth, intervalMs]);

  return { health, overall, services, lastChecked, checking, refresh: fetchHealth };
}
